import type {MerchantResponse} from "../../../api";
import {isDebtCabinetMerchant, isMirroredMerchant} from "../../../api/admin/merchants";

// Строки для показа в карточке мерчанта. Значения приходят с бэкенда
// десятичными строками и такими же остаются: во float их здесь не переводят.

export function formatMerchantBalance(merchant: MerchantResponse): string {
    const amount = merchant.amount || "0";
    return merchant.currency ? `${amount} ${merchant.currency}` : amount;
}

// Заявки мерчанта, привязанного к кабинету, приходят с площадки, и две
// настройки карточки к ним не применяются: срок брони — у каждой заявки свой и
// приезжает вместе с ней; число платежей — заявка кабинета дробится без лимита,
// сколько платежей делать, решает трейдер.
//
// Обе остаются видимыми и выключенными, а не прячутся: спрятанное поле читается
// как «настройки нет», и через месяц кто-нибудь заведёт вторую. 18.08.2026
// вторая из них стоила простоя — четвёрка в «платежей на заявку» остановила
// создание заявок обоих кабинетов на два с половиной часа.
export function orderShapeComesFromCabinet(merchant: MerchantResponse | null): boolean {
    return merchant?.external != null;
}

export function shortChainValue(value: string): string {
    if (value.length <= 18) return value;
    return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

// Название режима расчётов для блока «Расчёты и долг».
//
// У мерчанта-зеркала режим третий: баланс не считается, а читается из книги
// кабинета. Ни овердрафт, ни предоплата к нему не применимы — обе меряют
// предоплату, которой здесь нет, — поэтому он называется своим именем, а не
// подставляется в один из двух прежних.
export function formatSettlementMode(merchant: MerchantResponse): string {
    if (isMirroredMerchant(merchant)) return "Зеркало кабинета (баланс читается)";
    // Четвёртое имя, а не подстановка в одно из прежних: у долгового кабинета
    // баланс — настоящий долг, но замок предоплаты к нему не применяется, и
    // назвать его «предоплатой» значило бы обещать блокировку, которой нет.
    if (isDebtCabinetMerchant(merchant)) return "Кабинет в долг (минус по нашей заявке)";
    return merchant.overdraftEnabled
        ? "Овердрафт (в долг)"
        : "Предоплата (с баланса)";
}

// Подстрока с ДЕЙСТВУЮЩИМИ настройками режима. Значения — строки с бэкенда
// (USDT), не парсим в float; показываем только активные для текущего режима.
//
// overdraftLimit значит разное в двух режимах, и это не косметика: у долгового
// мерчанта он только сигналит (решение ДМ 06.07), у предоплатного — ещё и
// блокирует взятие заявок (решение 21.07). Поэтому в предоплате он тоже
// действует и называется допуском, а не «спящим» лимитом долга.
// Уведомление в предоплате не настраивается (порога больше нет): оно загорается
// самим фактом ухода баланса в минус (ДМ 22.07).
export function formatSettlementSetting(merchant: MerchantResponse): string {
    const currency = merchant.currency || "USDT";
    if (isMirroredMerchant(merchant)) {
        // Ни лимита долга, ни допуска овердрафта: деньги здесь принадлежат
        // кабинету на стороне площадки, и настроек у нас для них нет.
        return "порогов нет — баланс читается из книги кабинета, разбивка ниже";
    }
    if (isDebtCabinetMerchant(merchant)) {
        // Расчёт гарантирован кабинетом, поэтому взятие заявок не гейтится ни при
        // каком минусе; гасят долг поступления на привязанный кошелёк.
        return "порогов нет — минус растёт по нашим заявкам, гасится поступлениями на привязанный кошелёк";
    }
    if (merchant.overdraftEnabled) {
        return merchant.overdraftLimit
            ? `лимит долга ${merchant.overdraftLimit} ${currency}`
            : "лимит не задан";
    }
    return `допуск овердрафта ${merchant.overdraftLimit ?? "0"} ${currency} — на сколько можно уйти в минус (дальше блок взятия)`;
}
